import { Box, SxProps, Theme } from "@mui/system";
import { Song } from "~/types";
import AddSongTile from "./AddSongTile";
import type { ActionsVariant } from "./songActionsFactory";
import SongTile, { SongTileCosmeticProps } from "./SongTile";


type BreakpointKey = "xs" | "sm" | "md" | "lg" | "xl";
type ResponsiveSizes = Partial<Record<BreakpointKey, number>>;

type Props = SongTileCosmeticProps & {
  playlist: Song[];
  variant: "grid" | "carousel";
  sx?: SxProps<Theme>;
  actionsVariant?: ActionsVariant;
  size?: number | ResponsiveSizes;
  showAddTile?: boolean;
};

const toGridTemplate = (size: number | ResponsiveSizes) => {
  if (typeof size === "number") return `repeat(auto-fit, ${size}px)`;
  const res: Partial<Record<BreakpointKey, string>> = {};
  (["xs", "sm", "md", "lg", "xl"] as BreakpointKey[]).forEach((bp) => {
    const v = (size as ResponsiveSizes)[bp];
    if (typeof v === "number") res[bp] = `repeat(auto-fit, ${v}px)`;
  });
  return res;
};

const SongTiles = ({
  playlist,
  variant,
  sx = {},
  size = 160,
  actionsVariant,
  ...cosmeticProps
}: Props) => {
  const gridTemplate = toGridTemplate(size);

  return (
    <Box
      className="hide-scrollbar"
      sx={
        variant === "grid"
          ? {
            display: "grid",
            gridTemplateColumns:
              typeof gridTemplate === "string" ? gridTemplate : gridTemplate,
            gridAutoRows: "auto",
            justifyContent: "flex-start",
            gap: 2,
            overflowX: "visible",
            ...sx,
          }
          : {
            display: "flex",
            flexDirection: "row",
            gap: 2,
            overflowX: { xs: "auto", md: "visible" },
            scrollSnapType: { xs: "x mandatory", md: "none" },
            ...sx,
          }
      }
    >
      {playlist.map((song) => (
        <SongTile
          key={song.id}
          song={song}
          playlist={playlist}
          size={size}
          actionsVariant={actionsVariant}
          {...cosmeticProps}
        />
      ))}

      {/* Add a fake tile at the end linking to the create-song flow */}
      <AddSongTile
        key="create-new"
        size={size}
      />
    </Box>
  );
};

export default SongTiles;
